/**
 * BankerAvatar — 2D animated SVG business professional character.
 *
 * Pure CSS animations via inline <style> tag. No external dependencies.
 * Respects prefers-reduced-motion.
 *
 * Story v3-7-4: 2D Animated Banker Character
 */

import React from 'react';

export type AvatarState = 'idle' | 'thinking' | 'speaking' | 'confident' | 'concerned' | 'greeting';
export type AvatarPersona = 'analyst' | 'banker' | 'copilot';
export type AvatarSize = 'sm' | 'md' | 'lg' | 'xl';

interface BankerAvatarProps {
  state: AvatarState;
  persona: AvatarPersona;
  size?: AvatarSize;
  /** 0–100 — how open the mouth is when speaking */
  mouthOpenness?: number;
}

const SIZE_MAP: Record<AvatarSize, number> = {
  sm: 64,
  md: 96,
  lg: 128,
  xl: 192,
};

// ── Shared animation names (one set used by all instances) ──────────────────
const ANIM = {
  idlePulse:   'avatarIdlePulse',
  eyeBlink:    'avatarEyeBlink',
  mouthAnim:   'avatarMouth',
  greetingBob: 'avatarGreeting',
  tiltAnim:    'avatarTilt',
} as const;

/** Inject shared keyframe styles into <head> exactly once. */
function ensureSharedStyles(): void {
  if (typeof document === 'undefined') return;
  if (document.getElementById('banker-avatar-keyframes')) return;
  const style = document.createElement('style');
  style.id = 'banker-avatar-keyframes';
  style.textContent = `
    @media (prefers-reduced-motion: no-preference) {
      @keyframes ${ANIM.idlePulse} {
        0%, 100% { transform: scale(1); }
        50%       { transform: scale(1.01); }
      }
      @keyframes ${ANIM.eyeBlink} {
        0%, 90%, 100% { transform: scaleY(1); }
        95%            { transform: scaleY(0.05); }
      }
      @keyframes ${ANIM.mouthAnim} {
        0%   { d: path("M 41 70 C 41 70 59 70 59 70"); }
        100% { d: path("M 41 70 C 41 76 59 76 59 70"); }
      }
      @keyframes ${ANIM.greetingBob} {
        0%   { transform: translateY(0); }
        30%  { transform: translateY(-4px); }
        60%  { transform: translateY(-1px); }
        80%  { transform: translateY(-3px); }
        100% { transform: translateY(0); }
      }
      @keyframes ${ANIM.tiltAnim} {
        0%   { transform: rotate(-2deg); }
        100% { transform: rotate(2deg); }
      }
    }
  `;
  document.head.appendChild(style);
}

/** Persona accent colours */
const ACCENT: Record<AvatarPersona, string> = {
  analyst: '#3B82F6', // blue-500
  banker: '#F59E0B',  // amber-500
  copilot: '#14B8A6', // teal-500
};

const BankerAvatar: React.FC<BankerAvatarProps> = ({
  state,
  persona,
  size = 'md',
  mouthOpenness = 0,
}) => {
  ensureSharedStyles();

  const px = SIZE_MAP[size];
  const accent = ACCENT[persona];

  // ── Derived geometry ────────────────────────────────────────────
  // All values are in an internal 100×120 coordinate space then
  // scaled to `px` via the SVG viewBox.

  /** Eye vertical shift for 'thinking' */
  const eyeShift = state === 'thinking' ? -3 : 0;

  /** Brow angle: negative = furrow (concerned), positive = raised (thinking/greeting) */
  const browsAngle = state === 'concerned' ? -6
    : (state === 'thinking' || state === 'greeting') ? 5
    : 0;

  /** Mouth curve — positive = smile, negative = frown */
  const mouthCurve = state === 'confident' ? 6
    : state === 'concerned' ? -4
    : 0;

  /**
   * Mouth open amount in px (SVG units).
   * `mouthOpenness` (0–100) drives it for speaking state,
   * otherwise the CSS animation drives it via a CSS var.
   */
  const mouthOpen = state === 'speaking'
    ? (mouthOpenness / 100) * 8
    : 0;

  // ── Animation selectors (use shared ANIM names) ─────────────────
  const groupAnim = state === 'idle'
    ? `${ANIM.idlePulse} 3s ease-in-out infinite`
    : state === 'greeting'
    ? `${ANIM.greetingBob} 0.5s ease-in-out`
    : state === 'thinking'
    ? `${ANIM.tiltAnim} 2s ease-in-out infinite alternate`
    : 'none';

  const eyeAnimStyle = `${ANIM.eyeBlink} 4s ease-in-out infinite`;
  const mouthAnimStyle = state === 'speaking' && mouthOpenness === 0
    ? `${ANIM.mouthAnim} 0.3s ease-in-out infinite alternate`
    : 'none';

  // ── Mouth path helpers ──────────────────────────────────────────
  const mouthY = 70;
  const mouthHalfW = 9;
  const openY = mouthY + Math.max(mouthOpen, 0);

  /** Build an SVG path for the mouth */
  const mouthPath = mouthOpen > 0
    // Open mouth: rounded rectangle
    ? `M ${50 - mouthHalfW} ${mouthY}
       Q ${50 - mouthHalfW} ${mouthY - 2} ${50 - mouthHalfW + 2} ${mouthY - 2}
       L ${50 + mouthHalfW - 2} ${mouthY - 2}
       Q ${50 + mouthHalfW} ${mouthY - 2} ${50 + mouthHalfW} ${mouthY}
       L ${50 + mouthHalfW} ${openY}
       Q ${50} ${openY + 3} ${50 - mouthHalfW} ${openY}
       Z`
    // Closed mouth: cubic bezier with curve
    : `M ${50 - mouthHalfW} ${mouthY}
       C ${50 - mouthHalfW} ${mouthY + mouthCurve} ${50 + mouthHalfW} ${mouthY + mouthCurve} ${50 + mouthHalfW} ${mouthY}`;

  return (
    <>
      <svg
        width={px}
        height={px}
        viewBox="0 0 100 120"
        xmlns="http://www.w3.org/2000/svg"
        aria-hidden="true"
        style={{
          display: 'block',
          animation: groupAnim,
          transformOrigin: '50px 60px',
          // Respect prefers-reduced-motion at the element level too
        }}
      >
        {/* ── Suit / collar ─────────────────────────────── */}
        {/* Jacket body */}
        <path
          d="M 20 115 L 20 90 Q 50 82 80 90 L 80 115 Z"
          fill="#1E293B"
        />
        {/* Shirt / collar */}
        <path
          d="M 38 88 L 50 108 L 62 88 L 56 82 L 50 86 L 44 82 Z"
          fill="#F8FAFC"
        />
        {/* Lapels with accent colour stripe */}
        <path
          d="M 38 88 L 30 100 L 20 90 L 20 115 L 37 115 L 38 88 Z"
          fill="#334155"
        />
        <path
          d="M 62 88 L 70 100 L 80 90 L 80 115 L 63 115 L 62 88 Z"
          fill="#334155"
        />
        {/* Accent tie / pocket square */}
        <path
          d="M 47 88 L 50 108 L 53 88 L 51.5 83 L 50 85 L 48.5 83 Z"
          fill={accent}
          opacity="0.85"
        />

        {/* ── Neck ──────────────────────────────────────── */}
        <rect x="43" y="78" width="14" height="12" rx="2" fill="#FBBF93" />

        {/* ── Head ──────────────────────────────────────── */}
        <ellipse
          cx="50"
          cy="46"
          rx="22"
          ry="24"
          fill="#FBBF93"
        />

        {/* Hair */}
        <path
          d="M 28 42 Q 28 22 50 22 Q 72 22 72 42
             Q 68 28 50 28 Q 32 28 28 42 Z"
          fill="#4B3C2A"
        />

        {/* ── Ears ──────────────────────────────────────── */}
        <ellipse cx="28" cy="46" rx="3.5" ry="5" fill="#F5AC7C" />
        <ellipse cx="72" cy="46" rx="3.5" ry="5" fill="#F5AC7C" />

        {/* ── Eyes (left, right) ────────────────────────── */}
        <g style={{ animation: eyeAnimStyle, transformOrigin: '38px 46px' }}>
          <ellipse
            cx="38"
            cy={46 + eyeShift}
            rx="4"
            ry="4.5"
            fill="white"
          />
          {/* Iris */}
          <circle cx="38" cy={46 + eyeShift} r="2.5" fill="#3B2F20" />
          {/* Pupil */}
          <circle cx="38.6" cy={45.4 + eyeShift} r="1" fill="white" />
        </g>

        <g style={{ animation: eyeAnimStyle, animationDelay: '0.1s', transformOrigin: '62px 46px' }}>
          <ellipse
            cx="62"
            cy={46 + eyeShift}
            rx="4"
            ry="4.5"
            fill="white"
          />
          <circle cx="62" cy={46 + eyeShift} r="2.5" fill="#3B2F20" />
          <circle cx="62.6" cy={45.4 + eyeShift} r="1" fill="white" />
        </g>

        {/* ── Eyebrows ──────────────────────────────────── */}
        {/* Left brow */}
        <line
          x1="33"
          y1={38 - eyeShift + (browsAngle > 0 ? browsAngle * 0.4 : browsAngle * 0.6)}
          x2="43"
          y2={38 - eyeShift - (browsAngle > 0 ? browsAngle * 0.4 : browsAngle * 0.6)}
          stroke="#4B3C2A"
          strokeWidth="2"
          strokeLinecap="round"
        />
        {/* Right brow */}
        <line
          x1="57"
          y1={38 - eyeShift - (browsAngle > 0 ? browsAngle * 0.4 : browsAngle * 0.6)}
          x2="67"
          y2={38 - eyeShift + (browsAngle > 0 ? browsAngle * 0.4 : browsAngle * 0.6)}
          stroke="#4B3C2A"
          strokeWidth="2"
          strokeLinecap="round"
        />

        {/* ── Nose ──────────────────────────────────────── */}
        <ellipse cx="50" cy="58" rx="2.5" ry="3" fill="#E8935A" opacity="0.7" />

        {/* ── Mouth ─────────────────────────────────────── */}
        {mouthOpen > 0 ? (
          /* Open mouth */
          <g>
            <path
              d={mouthPath}
              fill="#7C2020"
            />
            {/* Teeth */}
            <rect
              x={50 - mouthHalfW + 1}
              y={mouthY - 1}
              width={(mouthHalfW - 1) * 2}
              height={Math.min(mouthOpen * 0.6, 4)}
              rx="1"
              fill="white"
            />
          </g>
        ) : (
          /* Closed mouth — curve driven by mouthCurve */
          <path
            d={mouthPath}
            fill="none"
            stroke="#C06030"
            strokeWidth="1.8"
            strokeLinecap="round"
            style={
              state === 'speaking'
                ? { animation: mouthAnimStyle }
                : undefined
            }
          />
        )}

        {/* ── Subtle cheek blush (confident / greeting) ─ */}
        {(state === 'confident' || state === 'greeting') && (
          <>
            <ellipse cx="31" cy="54" rx="5" ry="3" fill="#F87171" opacity="0.18" />
            <ellipse cx="69" cy="54" rx="5" ry="3" fill="#F87171" opacity="0.18" />
          </>
        )}

        {/* ── Accent dot on lapel ───────────────────────── */}
        <circle cx="35" cy="96" r="2" fill={accent} opacity="0.9" />
      </svg>
    </>
  );
};

export default BankerAvatar;
